import React, { forwardRef } from 'react';
import { ReactComponent as ChevronDownIcon } from '@/img/chevron-down.svg';
import './UISelect.scss';

export interface UISelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface UISelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /**
   * Варіант стилю селекта
   */
  variant?: 'default' | 'error' | 'success';

  /**
   * Розмір селекта
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Повний width батьківського контейнера
   */
  fullWidth?: boolean;

  /**
   * Текст помилки (показується під селектом)
   */
  error?: string;

  /**
   * Лейбл над селектом
   */
  label?: string;

  /**
   * Опції для вибору
   */
  options?: UISelectOption[];

  /**
   * Placeholder текст (перша опція)
   */
  placeholder?: string;
}

const baseClass = 'UISelect';

const UISelect = forwardRef<HTMLSelectElement, UISelectProps>(
  (
    {
      variant = 'default',
      size = 'medium',
      className = '',
      fullWidth = false,
      error,
      label,
      options = [],
      placeholder = 'Select Option',
      children,
      ...props
    },
    ref,
  ) => {
    const selectClasses = [
      baseClass,
      `${baseClass}--${variant}`,
      `${baseClass}--${size}`,
      fullWidth && `${baseClass}--full-width`,
      error && `${baseClass}--error`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const containerClasses = [
      `${baseClass}__container`,
      fullWidth && `${baseClass}__container--full-width`,
    ]
      .filter(Boolean)
      .join(' ');

    const wrapperClasses = [
      `${baseClass}__wrapper`,
      fullWidth && `${baseClass}__wrapper--full-width`,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        {label && <label className={`${baseClass}__label`}>{label}</label>}

        <div className={wrapperClasses}>
          <select ref={ref} className={selectClasses} {...props}>
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}

            {options.map((option, index) => (
              <option
                key={option.value || index}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}

            {children}
          </select>

          <div className={`${baseClass}__arrow`}>
            <ChevronDownIcon className={`${baseClass}__chevron-icon`} />
          </div>
        </div>

        {error && <span className={`${baseClass}__error-text`}>{error}</span>}
      </div>
    );
  },
);

UISelect.displayName = 'UISelect';

export default UISelect;
